import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import styled, { css, keyframes } from "styled-components";

export type NotificationBadgePlacement = "top-start" | "top-end" | "bottom-start" | "bottom-end";
export type NotificationBadgeTone =
  | "error"
  | "warning"
  | "success"
  | "info"
  | "running"
  | "paused"
  | "muted"
  | "completed"
  | "ready"
  | "recording"
  | "cancelling"
  | "attention"
  | "exhausted"
  | "checking";

export interface NotificationBadgeProps {
  visible: boolean;
  children: ReactNode;
  placement?: NotificationBadgePlacement;
  tone?: NotificationBadgeTone;
  size?: string;
  offset?: string;
  pulse?: boolean;
  "aria-label"?: string;
  className?: string;
  style?: CSSProperties;
}

export interface UsePulseOnChangeOptions {
  durationMs?: number;
  pulseOnMount?: boolean;
}

const Wrapper = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
`;

const pulseRing = keyframes`
  0% {
    box-shadow: 0 0 0 0 var(--c6-notification-badge-pulse-start);
  }
  70% {
    box-shadow: 0 0 0 0.35rem var(--c6-notification-badge-pulse-end);
  }
  100% {
    box-shadow: 0 0 0 0 var(--c6-notification-badge-pulse-end);
  }
`;

const getToneColor = (theme: {
  color: {
    status: Record<NotificationBadgeTone, string>;
  };
}) => {
  return {
    error: theme.color.status.error,
    warning: theme.color.status.warning,
    success: theme.color.status.success,
    info: theme.color.status.info,
    running: theme.color.status.running,
    paused: theme.color.status.paused,
    muted: theme.color.status.muted,
    completed: theme.color.status.completed,
    ready: theme.color.status.ready,
    recording: theme.color.status.recording,
    cancelling: theme.color.status.cancelling,
    attention: theme.color.status.attention,
    exhausted: theme.color.status.exhausted,
    checking: theme.color.status.checking,
  };
};

const Dot = styled.span<{
  $placement: NotificationBadgePlacement;
  $tone: NotificationBadgeTone;
  $size: string;
  $offset: string;
  $pulse: boolean;
}>`
  position: absolute;
  width: ${({ $size }) => $size};
  height: ${({ $size }) => $size};
  border: 1px solid ${({ theme }) => theme.color.bg.surfaceRaised};
  border-radius: 999px;
  --c6-notification-badge-tone: ${({ $tone, theme }) => getToneColor(theme)[$tone]};
  --c6-notification-badge-pulse-start: color-mix(
    in srgb,
    var(--c6-notification-badge-tone) 45%,
    transparent
  );
  --c6-notification-badge-pulse-end: color-mix(
    in srgb,
    var(--c6-notification-badge-tone) 0%,
    transparent
  );
  background-color: var(--c6-notification-badge-tone);
  pointer-events: none;

  ${({ $offset, $placement }) => {
    const vertical = $placement.startsWith("top") ? "top" : "bottom";
    const horizontal = $placement.endsWith("start") ? "left" : "right";
    return css`
      ${vertical}: ${$offset};
      ${horizontal}: ${$offset};
    `;
  }}

  animation: ${({ $pulse }) => ($pulse ? pulseRing : "none")} 720ms ease-out 1;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export function NotificationBadge({
  visible,
  children,
  placement = "top-end",
  tone = "error",
  size = "0.5rem",
  offset = "-0.1rem",
  pulse = false,
  "aria-label": ariaLabel,
  className,
  style,
}: NotificationBadgeProps) {
  return (
    <Wrapper className={className} style={style}>
      {children}
      {visible ? (
        <Dot
          role={ariaLabel ? "status" : undefined}
          aria-label={ariaLabel}
          $placement={placement}
          $tone={tone}
          $size={size}
          $offset={offset}
          $pulse={pulse}
        />
      ) : null}
    </Wrapper>
  );
}

export function usePulseOnChange(
  value: unknown,
  { durationMs = 720, pulseOnMount = false }: UsePulseOnChangeOptions = {},
): boolean {
  const [active, setActive] = useState(false);
  const mountedRef = useRef(false);

  useEffect(() => {
    if (value == null) {
      mountedRef.current = true;
      return;
    }

    if (!mountedRef.current) {
      mountedRef.current = true;
      if (!pulseOnMount) return;
    }

    setActive(true);
    const timer = window.setTimeout(() => setActive(false), durationMs);
    return () => window.clearTimeout(timer);
  }, [durationMs, pulseOnMount, value]);

  return active;
}

NotificationBadge.displayName = "NotificationBadge";
